/**
 * Virtual Grid Calculation Utility
 * Transforms two 4x4 grids into a combined virtual grid based on rotation and relative positioning
 */

import type { DeviceLinkConfig, PositionMapEntry, HardwareConfig } from '../services/bluetooth/types'

export interface Position {
  row: number
  col: number
}

export interface VirtualGrid {
  rows: number
  cols: number
  scalePositions: PositionMapEntry[]
}

/**
 * Apply rotation transformation to a position
 * @param pos Original position in 4x4 grid
 * @param rotation Rotation angle (0, 90, 180, 270)
 * @param gridSize Size of the grid (default 4 for 4x4)
 * @returns Rotated position
 */
function applyRotation(pos: Position, rotation: 0 | 90 | 180 | 270, gridSize: number = 4): Position {
  const { row, col } = pos
  const maxIdx = gridSize - 1

  switch (rotation) {
    case 0:
      return { row, col }
    case 90:
      // Clockwise 90°: (row, col) -> (col, maxIdx - row)
      return { row: col, col: maxIdx - row }
    case 180:
      // 180°: (row, col) -> (maxIdx - row, maxIdx - col)
      return { row: maxIdx - row, col: maxIdx - col }
    case 270:
      // Clockwise 270° (or counter-clockwise 90°): (row, col) -> (maxIdx - col, row)
      return { row: maxIdx - col, col: row }
    default:
      return { row, col }
  }
}

/**
 * Translate a position by an offset
 */
function translatePosition(pos: Position, offset: Position): Position {
  return {
    row: pos.row + offset.row,
    col: pos.col + offset.col,
  }
}

/**
 * Calculate offset for secondary device based on relative position
 * @param relativePosition Where secondary is relative to primary
 * @param primaryRotation Primary device rotation
 * @param secondaryRotation Secondary device rotation
 * @param gridSize Size of each device grid (default 4)
 * @returns Offset to apply to secondary positions
 */
function calculateSecondaryOffset(
  relativePosition: 'right' | 'left' | 'below' | 'above',
  primaryRotation: 0 | 90 | 180 | 270,
  _secondaryRotation: 0 | 90 | 180 | 270,
  gridSize: number = 4
): Position {
  // Calculate the dimensions of the rotated primary grid
  const primaryRotatedDims = getRotatedDimensions(gridSize, gridSize, primaryRotation)

  switch (relativePosition) {
    case 'right':
      return { row: 0, col: primaryRotatedDims.cols }
    case 'left':
      return { row: 0, col: -gridSize }
    case 'below':
      return { row: primaryRotatedDims.rows, col: 0 }
    case 'above':
      return { row: -gridSize, col: 0 }
  }
}

/**
 * Get dimensions after rotation
 */
function getRotatedDimensions(
  rows: number,
  cols: number,
  rotation: 0 | 90 | 180 | 270
): { rows: number; cols: number } {
  if (rotation === 90 || rotation === 270) {
    return { rows: cols, cols: rows } // Swap dimensions
  }
  return { rows, cols }
}

/**
 * Calculate combined virtual grid dimensions
 */
function calculateCombinedDimensions(
  config: DeviceLinkConfig,
  gridSize: number = 4
): { rows: number; cols: number } {
  const { relativePosition, primaryRotation, secondaryRotation } = config

  const primaryDims = getRotatedDimensions(gridSize, gridSize, primaryRotation)
  const secondaryDims = getRotatedDimensions(gridSize, gridSize, secondaryRotation)

  switch (relativePosition) {
    case 'right':
    case 'left':
      // Side by side: max rows, sum of cols
      return {
        rows: Math.max(primaryDims.rows, secondaryDims.rows),
        cols: primaryDims.cols + secondaryDims.cols,
      }
    case 'below':
    case 'above':
      // Stacked: sum of rows, max cols
      return {
        rows: primaryDims.rows + secondaryDims.rows,
        cols: Math.max(primaryDims.cols, secondaryDims.cols),
      }
  }
}

/**
 * Create default position map for a 4x4 grid
 */
function createDefaultPositionMap(gridSize: number = 4): PositionMapEntry[] {
  const entries: PositionMapEntry[] = []
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const position = row * gridSize + col
      entries.push({
        row,
        col,
        ledIndex: position,
        hasLED: true,
      })
    }
  }
  return entries
}

/**
 * Calculate virtual grid for dual-device mode
 * @param config Layout configuration (rotation and relative position)
 * @param hardwareConfig Hardware configuration from device
 * @returns Virtual grid with transformed positions for all 32 scales
 */
export function calculateVirtualGrid(
  config: DeviceLinkConfig,
  hardwareConfig: HardwareConfig | null
): VirtualGrid {
  if (!hardwareConfig) {
    return { rows: 4, cols: 4, scalePositions: [] }
  }

  const { relativePosition, primaryRotation, secondaryRotation } = config
  const gridSize = hardwareConfig.gridRows // Assuming square grid

  // Calculate combined dimensions
  const { rows, cols } = calculateCombinedDimensions(config, gridSize)

  // Create default position maps for both devices
  const primaryPositions = createDefaultPositionMap(gridSize)
  const secondaryPositions = createDefaultPositionMap(gridSize)

  // Calculate offset for secondary device
  const secondaryOffset = calculateSecondaryOffset(
    relativePosition,
    primaryRotation,
    secondaryRotation,
    gridSize
  )

  // Transform all positions
  const scalePositions: PositionMapEntry[] = []

  // Transform primary positions (positions 0-15)
  for (let i = 0; i < primaryPositions.length; i++) {
    const entry = primaryPositions[i]
    const rotatedPos = applyRotation({ row: entry.row, col: entry.col }, primaryRotation, gridSize)

    scalePositions.push({
      row: rotatedPos.row,
      col: rotatedPos.col,
      ledIndex: entry.ledIndex,
      hasLED: entry.hasLED,
    })
  }

  // Transform secondary positions (positions 16-31)
  for (let i = 0; i < secondaryPositions.length; i++) {
    const entry = secondaryPositions[i]
    const rotatedPos = applyRotation({ row: entry.row, col: entry.col }, secondaryRotation, gridSize)
    const finalPos = translatePosition(rotatedPos, secondaryOffset)

    scalePositions.push({
      row: finalPos.row,
      col: finalPos.col,
      ledIndex: entry.ledIndex + 16, // Offset LED indices for secondary
      hasLED: entry.hasLED,
    })
  }

  return {
    rows,
    cols,
    scalePositions,
  }
}

/**
 * Get scale position in virtual grid by position index
 * @param virtualGrid Virtual grid
 * @param position Scale position index (0-31)
 * @returns Position entry or undefined
 */
export function getScalePosition(virtualGrid: VirtualGrid, position: number): PositionMapEntry | undefined {
  return virtualGrid.scalePositions[position]
}

/**
 * Check if a position is valid in the virtual grid
 */
export function isValidPosition(virtualGrid: VirtualGrid, row: number, col: number): boolean {
  return row >= 0 && row < virtualGrid.rows && col >= 0 && col < virtualGrid.cols
}
